import React from 'react';
import { MapTools, WorkspaceContext } from '.';

type MjollnirEvent = {
  srcEvent: {
    originalEvent: PointerEvent;
    preventDefault: () => void;
  };
};

function useWorkspace() {
  const context = React.useContext(WorkspaceContext);

  if (!context) {
    throw new Error(`useWorkspace must be used within a WorkspaceProvider`);
  }

  const {
    store,
    hoveredMarkerId,
    setHoveredMarkerId,
    addMarkerPoint,
    deleteMarkerPoint,
    clearMarkerPoints,
    setActiveMapTool,
    setSharedMap,
    setLoadedShareSlug,
    onShareMap,
    onShareMapDialogClose,
    startDrawObject,
    startEditObject,
    setObjectDraft,
    appendDraftPoint,
    clearObjectDraft,
    toggleObjectSnapping,
    startBoxSelect,
    setBatchSelectedIds,
    clearBatchSelection,
    setSmartSearchSelection,
    getWorkspaceStore,
  } = context;

  const enableMarkerPointTool = () => {
    setActiveMapTool(MapTools.ADD_MARKER_POINT_TOOL);
  };

  const enableDefaultTool = () => {
    setActiveMapTool(null);
  };

  const onClickMap = (
    { coordinate }: { coordinate: [number, number] },
    event: MjollnirEvent
  ) => {
    switch (store.activeMapTool) {
      case MapTools.ADD_MARKER_POINT_TOOL:
        addMarkerPoint({
          coordinate,
          label: 'New point',
        });
        event.srcEvent.preventDefault();
        break;
      case MapTools.OBJECT_DRAW_POINT:
      case MapTools.OBJECT_DRAW_LINE:
      case MapTools.OBJECT_DRAW_POLYGON:
      case MapTools.OBJECT_DRAW_CIRCLE:
        appendDraftPoint(coordinate);
        event.srcEvent.preventDefault();
        break;
      default:
        break;
    }
  };

  return {
    store,
    hoveredMarkerId,
    setHoveredMarkerId,
    onShareMap,
    onShareMapDialogClose,
    setSharedMap,
    setLoadedShareSlug,
    addMarkerPoint,
    deleteMarkerPoint,
    clearMarkerPoints,
    enableMarkerPointTool,
    enableDefaultTool,
    setActiveMapTool,
    startDrawObject,
    startEditObject,
    setObjectDraft,
    appendDraftPoint,
    clearObjectDraft,
    toggleObjectSnapping,
    startBoxSelect,
    setBatchSelectedIds,
    clearBatchSelection,
    setSmartSearchSelection,
    getWorkspaceStore,
    onClickMap,
  };
}

export { useWorkspace };
